import { HttpStatusCode } from "axios";
import { httpPost } from "../../../modules/Backend";
import { IPicklistItem } from "../List/DisposalsPickList";
import ConfirmModal, { ConfirmType } from "../Modal/ConfirmModal";
import { modalSignal } from "../Modal/ModalContainer";
import CancelPicklistModal from "../Modal/CancelPicklistModal";
import { useNavigate } from "react-router";
import { EntityNames } from "../../../enums/EntityNames";
import { DisposalLineItemStatusSmartEnum } from "../../../enums/Domain/DisposalLineItemStatusSmartEnum";

interface UsePickListActionsProps {
    disposalId: string | undefined;
    items: IPicklistItem[];
    maxBoxCountForOnDemandDisposal: number
}

export default function useDisposalPickListActions({ disposalId, items, maxBoxCountForOnDemandDisposal }: UsePickListActionsProps) {

    const navigate = useNavigate();

    function goToDisposal() {
        navigate(`/view/${EntityNames.disposals}/${disposalId}`, { replace: true });
    }

    const save = async () => {
        const payload = {
            disposalId: disposalId,
            items: items.map(x => ({
                id: x.id,
                status: x.status.value,
                hasBeenChecked: x.hasBeenChecked,
                notes: x.notes,
            }))
        };
        await httpPost(`api/${EntityNames.disposals}/savePicklist`, payload);
    };

    const handleCancel = async (notes?: string) => {
        const payload = {
            disposalId: disposalId,
            cancellationNotes: notes ?? null
        };
        const result = await httpPost(`api/${EntityNames.disposals}/cancelPicklist`, payload);
        if (result.status === HttpStatusCode.Ok) {
            goToDisposal();
        }
    };

    const submit = () => {
        const shouldCancel = items.length > 0 && items.every((x: IPicklistItem) =>
            x.status.value === DisposalLineItemStatusSmartEnum.Cancelled.value
        );

        const handle = async (cancellationNotes: string | undefined) => {
            if (shouldCancel) {
                await handleCancel(cancellationNotes);
            } else {
                const payload = {
                    disposalId: disposalId,
                    items: items.map(x => ({
                        id: x.id,
                        hasBeenChecked: x.hasBeenChecked,
                        status: x.status.value,
                        notes: x.notes,
                    }))
                };
                const result = await httpPost(`api/${EntityNames.disposals}/saveAndProcessPicklist`, payload);
                if (result.status === HttpStatusCode.Ok) {
                    goToDisposal();
                }
            }
        };

        const heading = shouldCancel ? "Warning" : "Complete Disposal?";
        const information = [
            "This action cannot be undone",
            "Any cancelled boxes will not be considered for disposal",
            "Any lines marked as 'Full Pallet' will trigger disposal of the entire outer cube"
        ];

        if (items.length > maxBoxCountForOnDemandDisposal) {
            information.push(`Because there are more than ${maxBoxCountForOnDemandDisposal} packing boxes, disposal will be completed at end of day`);
        }

        const modal = shouldCancel
            ? <CancelPicklistModal heading={heading} information={"You have cancelled all boxes, this will cancel the entire disposal"} ContinueFunc={handle} />
            : <ConfirmModal type={ConfirmType.Destructive} heading={heading} information={information} ContinueFunc={handle} />;

        modalSignal.value = modal;
    };

    const cancel = () => {
        modalSignal.value = (
            <CancelPicklistModal
                heading="Cancel Disposal?"
                information="This action cannot be undone"
                ContinueFunc={handleCancel}
                type={ConfirmType.Destructive}
            />
        );
    };

    return {
        save,
        submit,
        cancel
    };
}